import React, { type FC, useEffect, useState } from 'react';
import { useCoordination } from '@atlassiansox/engagekit-ts';
import { FormattedMessage, injectIntl } from 'react-intl-next';

import { SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightMedia,
	SpotlightPrimaryAction,
	SpotlightSecondaryAction,
} from '@atlaskit/spotlight';
import Image from '@atlaskit/image';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import {
	VERIFIED_CONTENT_PULSE_KEY,
	EventSourceName,
} from '@confluence/automation-discovery/entry-points/constants.ts';
import { useAutomationPermissionService } from '@confluence/automation-menu/entry-points/AutomationPermissionService.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useCoordinationClient } from '@confluence/engagement-provider';
import { VerifiedContentSpotlight } from '@confluence/automation-discovery/entry-points/VerifiedContentAutomationSpotlight.ts';
import { AutomationDiscoveryHeader } from '@confluence/automation-discovery/entry-points/AutomationDiscoveryHeader.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { fg } from '@confluence/feature-gating';

import { sharedi18n } from '../sharedi18n.ts';

import type { RendererContentStateComponentProps } from './types.ts';
import { RendererContentStateComponent } from './RendererContentStateComponent.tsx';
import { StyledBorder } from './StyledBorder.tsx';

const styles = cssMap({
	headerContent: {
		minWidth: '0px',
		flex: '1 1 auto',
	},
	headerText: {
		whiteSpace: 'normal',
	},
	bodyContainer: {
		whiteSpace: 'normal',
	},
});

const setUserHasSeenVerifiedContentSpotlight = (): void => {
	localStorage.setItem(keys.AUTOMATION_DISCOVER_VERIFIED_CONTENT, true);
};

const hasUserSeenVerifiedContentSpotlight = (): boolean => {
	return localStorage.getItemAsBoolean(keys.AUTOMATION_DISCOVER_VERIFIED_CONTENT);
};

const RendererContentStateWithSpotlightImpl: FC<RendererContentStateComponentProps> = ({
	contentId,
	contentState,
	lastUpdatedInfo,
	isDraft,
	hasSeparator = true,
}) => {
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);
	const coordinationClient = useCoordinationClient();
	const [pulseStarted, stopPulse] = useCoordination(coordinationClient, VERIFIED_CONTENT_PULSE_KEY);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const spaceKey = useSpaceKey();
	const { edition } = useSessionData();

	const isPremium = ConfluenceEdition.PREMIUM === edition;
	const { canManageAutomations } = useAutomationPermissionService(spaceKey || '');

	const handleProceed = () => {
		setIsSpotlightActive(false);
		if (pulseStarted) {
			void stopPulse();
		}
		window.open(
			`/wiki/spaces/${spaceKey}/settings/automation#/rule/template?templateId=confluence_notify_template_7`,
			'_blank',
		);
		setUserHasSeenVerifiedContentSpotlight();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'automationDiscoveryVerifiedContentProceed',
				source: EventSourceName.VERIFED_CONTENT,
				attributes: {
					spaceKey,
				},
			},
		}).fire();
	};

	const handleDismiss = () => {
		setIsSpotlightActive(false);
		if (pulseStarted) {
			void stopPulse();
		}
		setUserHasSeenVerifiedContentSpotlight();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'automationDiscoveryVerifiedContentDismiss',
				source: EventSourceName.VERIFED_CONTENT,
				attributes: {
					spaceKey,
				},
			},
		}).fire();
	};

	const showSpotlight = () => {
		// Spotlight is disabled
		setIsSpotlightActive(false);
	};

	const isVerifiedContentState =
		contentState?.restrictionLevel === 'PAGE_OWNER' && contentState.name === 'Verified';

	useEffect(() => {
		if (
			isPremium &&
			isVerifiedContentState &&
			!hasUserSeenVerifiedContentSpotlight() &&
			canManageAutomations
		) {
			setTimeout(() => {
				showSpotlight();
			}, 1);
		}
	}, [isPremium, isVerifiedContentState, canManageAutomations]);

	return (
		<>
			{fg('confluence_frontend_spotlight_ads_migration') ? (
				<PopoverProvider>
					<PopoverTarget>
						<RendererContentStateComponent
							contentId={contentId}
							contentState={contentState}
							lastUpdatedInfo={lastUpdatedInfo}
							isDraft={isDraft}
							hasSeparator={hasSeparator}
						/>
					</PopoverTarget>
					<PopoverContent
						dismiss={handleDismiss}
						isVisible={isSpotlightActive}
						placement="bottom-start"
						strategy="absolute"
					>
						{
							/* TODO A4C-3826: Remove this spotlight */
							isSpotlightActive && (
								<SpotlightCard testId="verified-content-spotlight">
									<SpotlightHeader>
										<Box xcss={styles.headerContent}>
											<SpotlightHeadline>
												<Box xcss={styles.headerText}>
													<FormattedMessage {...sharedi18n.newSpotlightTitle} />
												</Box>
											</SpotlightHeadline>
										</Box>
										<SpotlightControls>
											<SpotlightDismissControl onClick={handleDismiss} />
										</SpotlightControls>
									</SpotlightHeader>
									<SpotlightMedia testId="verified-content-spotlight-media">
										<Image src={AutomationDiscoveryHeader} alt="" width="100%" />
									</SpotlightMedia>
									<SpotlightBody>
										<Box xcss={styles.bodyContainer}>
											<FormattedMessage {...sharedi18n.newSpotlightBody} />
										</Box>
									</SpotlightBody>
									<SpotlightFooter>
										<SpotlightActions>
											<SpotlightSecondaryAction onClick={handleProceed}>
												<FormattedMessage {...sharedi18n.newSpotlightCta} />
											</SpotlightSecondaryAction>
											<SpotlightPrimaryAction onClick={handleDismiss}>
												<FormattedMessage {...sharedi18n.newSpotlightDismiss} />
											</SpotlightPrimaryAction>
										</SpotlightActions>
									</SpotlightFooter>
								</SpotlightCard>
							)
						}
					</PopoverContent>
				</PopoverProvider>
			) : (
				<>
					<SpotlightTarget name="verified-content-state">
						<RendererContentStateComponent
							contentId={contentId}
							contentState={contentState}
							lastUpdatedInfo={lastUpdatedInfo}
							isDraft={isDraft}
							hasSeparator={hasSeparator}
						/>
					</SpotlightTarget>
					<SpotlightTransition>
						{
							/* TODO A4C-3826: Remove this spotlight */
							isSpotlightActive && (
								<VerifiedContentSpotlight
									handleDismiss={handleDismiss}
									handleProceed={handleProceed}
								/>
							)
						}
					</SpotlightTransition>
				</>
			)}
			{/* Move the following border back to RendererContentStateComponent when removing spotlight */}
			{!isDraft && hasSeparator ? <StyledBorder /> : null}
		</>
	);
};

export const RendererContentStateWithSpotlight = injectIntl(RendererContentStateWithSpotlightImpl);
